import { LogoAdelante } from '@/components/layout/LogoAdelante';
import { Button } from '@/components/ui/button';
import { cliente, guardarSesionLab } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { Delete } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * `/lab-login` — Pantalla de autenticación por PIN de 4 dígitos.
 *
 * Pensada para operarios de laboratorio que NO tienen email corporativo.
 * El admin configura el PIN desde `/admin/lab-pin`. El operario solo ve
 * un teclado numérico grande y 4 puntos de progreso.
 *
 * Comportamiento:
 *   - Al teclear el 4to dígito, auto-envía.
 *   - En éxito: guarda JWT en localStorage, redirige a `/lab/muestras`.
 *   - En error: shake + limpia + muestra mensaje.
 *   - Lockout: si el backend devuelve 429 (bloqueo temporal), muestra
 *     hasta cuándo está bloqueado y deshabilita el teclado.
 *
 * No usa MSAL — esta pantalla es accesible sin login Entra ID.
 */

const TECLAS_NUMERICAS = [
  { id: 't1', valor: '1' },
  { id: 't2', valor: '2' },
  { id: 't3', valor: '3' },
  { id: 't4', valor: '4' },
  { id: 't5', valor: '5' },
  { id: 't6', valor: '6' },
  { id: 't7', valor: '7' },
  { id: 't8', valor: '8' },
  { id: 't9', valor: '9' },
  { id: 'tempty', valor: '' },
  { id: 't0', valor: '0' },
  { id: 'tborrar', valor: 'borrar' },
] as const;

export function LabLoginPantalla() {
  const navegar = useNavigate();
  const [pin, setPin] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  const enviar = useCallback(
    async (pinCompleto: string) => {
      setCargando(true);
      setError(null);
      try {
        const r = await cliente.authLab.login({ pin: pinCompleto });
        if (r.data) {
          guardarSesionLab(r.data.token, r.data.etiqueta, r.data.expira_en);
          toast.success(`Bienvenida ${r.data.etiqueta}`);
          navegar('/lab/muestras');
        } else {
          const msg = r.error?.mensaje ?? 'PIN incorrecto';
          setError(msg);
          setPin('');
          setShake(true);
          setTimeout(() => setShake(false), 600);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error desconocido';
        setError(msg);
        setPin('');
        setShake(true);
        setTimeout(() => setShake(false), 600);
      } finally {
        setCargando(false);
      }
    },
    [navegar],
  );

  // Auto-enviar al llegar a 4 dígitos
  useEffect(() => {
    if (pin.length === 4 && !cargando) {
      void enviar(pin);
    }
  }, [pin, cargando, enviar]);

  const teclear = (digito: string) => {
    if (cargando) return;
    setError(null);
    setPin((prev) => (prev.length < 4 ? prev + digito : prev));
  };

  const borrar = () => {
    if (cargando) return;
    setError(null);
    setPin((prev) => prev.slice(0, -1));
  };

  // Soporte de teclado físico (útil si el lab tiene teclado conectado).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (cargando) return;
      if (/^[0-9]$/.test(e.key)) {
        setError(null);
        setPin((prev) => (prev.length < 4 ? prev + e.key : prev));
      } else if (e.key === 'Backspace') {
        setError(null);
        setPin((prev) => prev.slice(0, -1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cargando]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0A0A] px-4 py-8">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-6 flex flex-col items-center gap-3">
          <LogoAdelante altura={28} />
          <span className="text-sm font-bold tracking-widest text-zinc-900">ADELANTE.</span>
          <span className="text-xs uppercase tracking-widest text-zinc-500">Laboratorio</span>
        </div>

        <h1 className="mb-1 text-center text-xl font-semibold text-zinc-900">Ingresá tu PIN</h1>
        <p className="mb-6 text-center text-sm text-zinc-500">4 dígitos</p>

        {/* Puntos de progreso */}
        <div
          className={cn(
            'mb-6 flex justify-center gap-4 transition-transform',
            shake && 'animate-shake',
          )}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={cn(
                'h-4 w-4 rounded-full border-2 transition-all',
                pin.length > i
                  ? 'border-emerald-600 bg-emerald-600 scale-110'
                  : 'border-zinc-300 bg-transparent',
                error && 'border-red-500 bg-red-500/20',
              )}
            />
          ))}
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-center text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Teclado numérico */}
        <div className="grid grid-cols-3 gap-3">
          {TECLAS_NUMERICAS.map((t) => {
            if (t.valor === '') return <div key={t.id} />;
            if (t.valor === 'borrar') {
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={borrar}
                  disabled={cargando || pin.length === 0}
                  className="flex h-16 items-center justify-center rounded-xl bg-zinc-100 text-zinc-700 transition active:scale-95 active:bg-zinc-200 disabled:opacity-40 disabled:active:scale-100"
                  aria-label="Borrar"
                >
                  <Delete className="h-6 w-6" />
                </button>
              );
            }
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => teclear(t.valor)}
                disabled={cargando}
                className="h-16 rounded-xl bg-zinc-100 text-2xl font-semibold text-zinc-900 transition active:scale-95 active:bg-zinc-200 disabled:opacity-40 disabled:active:scale-100"
                aria-label={`Dígito ${t.valor}`}
              >
                {t.valor}
              </button>
            );
          })}
        </div>

        {cargando && <div className="mt-4 text-center text-xs text-zinc-500">Verificando…</div>}

        <div className="mt-6 border-t pt-4 text-center">
          <Button
            variant="link"
            size="sm"
            onClick={() => navegar('/')}
            className="text-xs text-zinc-500"
          >
            ¿Sos administrador u operador? Entrá con tu cuenta de correo
          </Button>
        </div>
      </div>

      <p className="mt-6 text-xs text-zinc-500">AD Concreto · Adelante Desarrollos</p>

      {/* Animación shake */}
      <style>{`
        @keyframes shake-x {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-shake { animation: shake-x 0.5s ease-in-out; }
      `}</style>
    </div>
  );
}
